import { mergeObjects } from '@prophecy/utils/data';
import { SCHEMA_VERSION } from './schema';
import { ConsoleTransport } from './transport';
import { ActorType } from './types';
import { newEventId, currentISOTimeStamp } from './utils';
const UI_SERVICE = 'ui';
/**
 * Framework-agnostic metrics service: holds identify + context, assembles the event
 * envelope, and hands it to a transport. Import the {@link metrics} alias from anywhere.
 */
class MetricsService {
    static enabled = false;
    static service = UI_SERVICE;
    static version = 'unknown';
    static environment;
    static getIds;
    static transport = new ConsoleTransport();
    /** Immutable actor identity, set once on init. */
    static identifyData = null;
    /** Mutable context envelope. */
    static contextData = {};
    /** Configure framework-level fields. Called once on init by the host app. */
    static configure(config) {
        this.service = config.service ?? this.service;
        this.version = config.version ?? this.version;
        this.environment = config.environment ?? this.environment;
        this.enabled = config.enabled ?? this.enabled;
        this.getIds = config.getIds ?? this.getIds;
        if (config.transport) {
            this.transport = config.transport;
        }
    }
    static setEnabled(enabled) {
        this.enabled = enabled;
    }
    /** Set the immutable actor identity (e.g. after login). */
    static identify(data) {
        this.identifyData = { ...data };
    }
    /** Shallow-merge values into the context. */
    static appendContext(patch) {
        this.contextData = { ...this.contextData, ...patch };
    }
    /** Remove keys from the context, e.g. `removeContext(['teamId', 'projectId'])`. */
    static removeContext(keys) {
        keys.forEach((key) => {
            delete this.contextData[key];
        });
    }
    /** Replace the entire context with new values. */
    static replaceContext(context) {
        this.contextData = { ...context };
    }
    /** Read-only snapshot of the current context. */
    static getContext() {
        return { ...this.contextData };
    }
    /**
     * Track an event. No-op when disabled. Fire-and-forget.
     *
     * `payload` is typed to the given `eventName`. Pass `args.context` to override the
     * stored context for this event only — see {@link TrackOptions.mergeContext}. Pass
     * `args.actorType` to override the actor type (defaults to `user`).
     */
    static track(args, options = {}) {
        if (!this.enabled)
            return;
        const event = this.buildEvent(args, options);
        void this.transport.send(event).catch((error) => {
            console.error('[metrics] transport error', error);
        });
    }
    /** Per-event context: stored context, optionally overridden by `args.context`. Never mutates stored. */
    static resolveContext(override, mergeContext = true) {
        if (!override)
            return this.contextData;
        return mergeContext ? mergeObjects(this.contextData, override) : override;
    }
    /**
     * Per-event actor: stored `identify()` identity mapped to the wire {@link Actor} shape.
     * `type` resolves as override → stored actorType → {@link ActorType.user} (most UI events
     * are user-driven, so `user` is the default; pass `actorType` for `system`/`AI`/etc.).
     */
    static resolveActor(actorTypeOverride) {
        const { userId, actorType, ...rest } = this.identifyData ?? {};
        const actor = {
            ...rest,
            type: actorTypeOverride ?? actorType ?? ActorType.user
        };
        if (userId)
            actor.id = userId;
        return actor;
    }
    /** Assemble the full {@link MetricEvent} from caller intent + stored identify/context + auto-fields. */
    static buildEvent(args, options) {
        const event = {
            schemaVersion: SCHEMA_VERSION,
            eventId: newEventId(),
            // Wire field is the nominal enum; assert across from the string-union `EventNameValue`.
            eventName: args.eventName,
            eventType: args.eventType,
            time: args.time ?? currentISOTimeStamp(),
            source: { service: this.service, version: this.version, environment: this.environment },
            // `error` folds into the payload; enforced-per-event at the `track()` boundary, so cast here.
            payload: { ...args.payload, ...(args.error ? { error: args.error } : {}) }
        };
        event.actor = this.resolveActor(args.actorType);
        event.context = { ...this.getIds?.(), ...this.resolveContext(args.context, options.mergeContext) };
        return event;
    }
}
export const metrics = MetricsService;
